import * as React from 'react'

import { attachSpellcheck } from './spell/attach'
import { spellSupported } from './spell/client'
import { assistAt } from './typing-assist'

/*
 * Liga a ajuda de digitação (`typing-assist.ts`) num <input>/<textarea> de texto livre.
 * A troca passa por `execCommand('insertText')`, que entra na pilha de desfazer do
 * navegador: Ctrl+Z volta a palavra como foi digitada. O corretor ortográfico
 * (sublinhado + sugestões no botão direito) é o próprio do SUPPLY (`spell/`), que
 * não depende do navegador; o nativo só fica ligado onde o nosso não roda.
 */

type TextField = HTMLInputElement | HTMLTextAreaElement

let applying = false

function onInput(e: Event) {
  const ev = e as InputEvent
  if (applying || ev.isComposing) return
  if (ev.inputType !== 'insertText' && ev.inputType !== 'insertLineBreak') return
  if (ev.inputType === 'insertText' && ev.data?.length !== 1) return
  const el = e.currentTarget as TextField
  const caret = el.selectionStart
  if (caret === null || caret !== el.selectionEnd) return
  const fix = assistAt(el.value, caret)
  if (!fix) return

  applying = true
  try {
    el.setSelectionRange(fix.start, fix.end)
    // Sem execCommand (navegador antigo), melhor não corrigir do que perder o desfazer.
    const done = document.execCommand('insertText', false, fix.replacement)
    const shift = done ? fix.replacement.length - (fix.end - fix.start) : 0
    el.setSelectionRange(caret + shift, caret + shift)
  } finally {
    applying = false
  }
}

/** Ref de callback estável: liga correção e corretor no elemento (React 19 chama a limpeza ao desmontar). */
export function typingAssistRef(el: TextField | null) {
  if (!el) return
  el.addEventListener('input', onInput)
  const detachSpell = attachSpellcheck(el)
  return () => {
    el.removeEventListener('input', onInput)
    detachSpell()
  }
}

/** Props nativas dos textos livres: maiúscula do teclado do celular; corretor nativo só sem o nosso. */
export function typingAssistProps(enabled: boolean) {
  return { spellCheck: enabled && !spellSupported(), autoCapitalize: enabled ? 'sentences' : 'off' } as const
}

/**
 * Para os componentes base: devolve `ref` (já unindo a ref de quem chama) e as props nativas.
 * `enabled = false` desliga tudo (campo de código, e-mail, número, busca, CSV).
 */
export function useTypingAssist<T extends TextField>(enabled: boolean, forwarded?: React.Ref<T>) {
  const ref = React.useCallback(
    (el: T | null) => {
      if (typeof forwarded === 'function') forwarded(el)
      else if (forwarded) forwarded.current = el
      const detach = enabled ? typingAssistRef(el) : undefined
      return () => {
        detach?.()
        if (typeof forwarded === 'function') forwarded(null)
        else if (forwarded) forwarded.current = null
      }
    },
    [enabled, forwarded],
  )
  return { ref, ...typingAssistProps(enabled) }
}
